import axios from 'axios';

import { handleResponseError, initialState } from '@/Helpers.js';
import Model from '@/models/Model.js';
import deadlineReminderService from '@/services/deadlineReminderService.js';

const deadlineReminderClass = 'App\\Notifications\\UpcomingDeadlineNotification';
const staleFetchAttempts = 3;

export default class BellNotification extends Model {
    static entity = 'bell_notifications';

    static modelName = 'BellNotification';

    static primaryKey = 'notification_id';

    static reminderReadRevision = 0;

    static reminderReadWrites = new Set();

    static state() {
        return {
            counter: 0,
            page: 1,
            last_page: 1,
        };
    }

    static fields() {
        return {
            id: this.attr(null),
            notification_id: this.attr(null),
            course_id: this.attr(null),
            activity_id: this.attr(null),
            thread_id: this.attr(null),
            title: this.attr(null),
            body: this.attr(null),
            creator: this.attr(null),
            type: this.attr(null),
            read: this.boolean(null),
            course_code: this.attr(null),
            course_title: this.attr(null),
            due_at: this.attr(null),
            deadline: this.attr(null),
            already_submitted: this.boolean(false),
            class: this.attr(null),
            created_at: this.attr(null),

            ...super.fields(),
        };
    }

    static async fetch(page = 1) {
        try {
            for (let attempt = 0; attempt < staleFetchAttempts; attempt += 1) {
                const revision = this.reminderReadRevision;
                const { response } = await this.api().get(`/api/notifications?page=${page}`, { save: false });
                // A response from before a reminder write must not restore its old read state.
                // Reload the same page once the write settles so paginated rows are not lost.
                if (revision !== this.reminderReadRevision) {
                    await Promise.allSettled([...this.reminderReadWrites]);
                    continue;
                }
                await this.insertOrUpdate({ data: response.data.data });
                await this.commit(state => {
                    // Counter mirrors the aggregated inbox tiles (unread_count); fall back to
                    // the paginated total for older payloads.
                    state.counter = response.data.meta?.unread_count ?? response.data.meta?.total ?? 0;
                    state.last_page = response.data.meta?.last_page;
                });
                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async setReminderReadStatus(notification, read) {
        this.reminderReadRevision += 1;
        const write = (async () => {
            try {
                await deadlineReminderService.setReadStatus([notification.notification_id], read);
                await notification.$update({ read });
            } finally {
                this.reminderReadRevision += 1;
            }
        })();
        this.reminderReadWrites.add(write);
        try {
            await write;
        } finally {
            this.reminderReadWrites.delete(write);
        }
    }

    async markAsRead() {
        if (this.class === deadlineReminderClass) {
            await BellNotification.setReminderReadStatus(this, true);
            return;
        }

        this.$update({ read: true });
        await axios.put('/api/mark-as-read', {
            readable_id: this.id,
            readable_type: this.class,
        });
    }

    static async markMultipleAsRead(type, ids) {
        if (type === deadlineReminderClass) {
            await deadlineReminderService.setReadStatus(ids, true);
            return;
        }

        await axios.put('/api/mark-as-read', {
            readable_type: type,
            readable_ids: ids,
        });
    }

    async markAsUnread() {
        if (this.class === deadlineReminderClass) {
            await BellNotification.setReminderReadStatus(this, false);
            return;
        }

        await axios.put('/api/mark-as-unread', {
            readable_id: this.id,
            readable_type: this.class,
        });
    }

    get drawer_styles() {
        let styles;

        if (this.type === 'Deadline Reminder') {
            return {
                label: 'Deadline Reminder',
                style: 'background-color:#335CE5',
                icon: 'unicons uil uil-bell',
                actionButtonLabel: 'View Activity',
            };
        }

        if (this.type === 'Activity Update') {
            styles = {
                label: 'Activity Update',
                style: 'background-color:#335CE5',
                icon: 'unicons uil uil-file-plus is-size-8',
                actionButtonLabel: 'View Activity',
            };
        }

        if (this.type === 'Course Announcement') {
            styles = {
                label: 'Course Announcement',
                style: 'background-color:#ECA807',
                icon: 'unicons uil uil-megaphone is-size-8',
                actionButtonLabel: 'View Course',
            };
        }

        if (this.type === 'Message') {
            styles = {
                label: 'Message',
                style: 'background-color:#7160A6',
                icon: 'unicons uil uil-envelope is-size-8',
                actionButtonLabel: 'View Message',
            };
        }

        if (this.type === 'Discussion Reply') {
            styles = {
                label: 'Discussion Reply',
                style: 'background-color:#0EA5E9',
                icon: 'unicons uil uil-comment-dots is-size-8',
                actionButtonLabel: 'View Discussions',
            };
        }

        return styles;
    }

    get inbox_link() {
        if (this.type === 'Deadline Reminder') {
            return `/courses/${this.course_id}/activities/${this.activity_id}`;
        }

        if (this.type === 'Activity Update') {
            return `/inbox/announcements/course/${this.course_id}/activity-update/${this.id}`;
        }

        if (this.type === 'Course Announcement') {
            return `/inbox/announcements/course/${this.course_id}/course-announcement/${this.id}`;
        }

        if (this.type === 'Message') {
            return `/inbox/messages/${this.id}`;
        }

        if (this.type === 'Discussion Reply') {
            // Same path as the `discussion-thread-view` route; older rows without a thread fall back to the inbox list.
            if (this.course_id && this.activity_id && this.thread_id) {
                return `/courses/${this.course_id}/activities/${this.activity_id}/discussion/${this.thread_id}`;
            }

            return '/inbox/discussions';
        }

        return null;
    }

    get current_page() {
        return initialState('bell_notification.page');
    }

    get formatted_created_at() {
        if (!this.created_at) {
            return 'N/A';
        }

        return this.timestamp('created_at').format('ddd M/D [at] h:mm A');
    }

    get relative_created_at() {
        if (!this.created_at) {
            return null;
        }

        return this.timestamp('created_at').fromNow();
    }
}
